import { ShopCover } from "./ShopCover";
import { deliveryLine } from "../../../../../lib/ordering-visuals/deliveryFees";
/**
 * Discovery store card (web) — the card the Order Food / Shop Local browse
 * grid is built from.
 *
 * Deliberately NOT ./StoreCard, for the same reason mobile keeps
 * `DiscoveryStoreCard` separate from `components/StoreCard`
 * (app/(tabs)/order.tsx:162): the canonical card has no slot for sponsored
 * state, live offer banners, distance or the "opens at" line, all of which
 * the storefront needs. ./StoreCard stays the right choice for rails and
 * cross-page grids, and is still used for "Recently viewed" here.
 *
 * Layout intent — the desktop lesson from the app redesign is that extra
 * width buys MORE CARDS SIDE BY SIDE, not bigger ones. So this card has a
 * fixed image aspect ratio and a restrained, near-constant body height at
 * every breakpoint; only the column count changes. That also means the
 * skeleton can match it exactly and the grid never shifts on load.
 *
 * Business logic lives in ./store-utils — this file only arranges it.
 */
import { Link } from "wouter";
import { Clock, MapPin, Moon, ShoppingBag, Star, Truck } from "lucide-react";
import { cn } from "@/lib/utils";
import { Chip } from "./primitives";
import { ELEVATION, FOCUS_RING, RADII, TYPE } from "./tokens";
import {
  formatDistance, formatNextOpen, formatOfferWithMin, hasFreeDelivery,
  isSponsored, storeHref, storeIsOpen, type StoreItem,
} from "./store-utils";

/** A store row plus the client-derived distance the browse page computes. */
export type BrowseStore = StoreItem & {
  /** Miles from the user, or null when location is unknown. */
  distMi?: number | null;
  storeLatitude?: number | null;
  storeLongitude?: number | null;
};

/**
 * Solid gold stars, matching the app's discovery card and shop header.
 * Absent entirely until the shop has approved reviews — never a zero-star row,
 * which reads as a bad rating rather than "no data".
 */
export function StarRating({
  avgRating, reviewCount, size = 11,
}: { avgRating?: number; reviewCount?: number; size?: number }) {
  const count = reviewCount ?? 0;
  const avg = avgRating ?? 0;
  if (count === 0) {
    return <span className={cn(TYPE.footnote, "text-slate-400")}>No reviews yet</span>;
  }
  return (
    <span className="inline-flex items-center gap-0.5">
      <Star size={12} className="text-amber-500" fill="currentColor" aria-hidden />
      <span className={cn(TYPE.footnote, "text-slate-500 ml-1")}>
        {avg.toFixed(1)} ({count})
      </span>
    </span>
  );
}

/** Dark translucent tag for overlaying on photography. */
function ImageTag({ children }: { children: React.ReactNode }) {
  return (
    <span className={cn(
      TYPE.caption, RADII.pill,
      "inline-flex items-center gap-1 bg-black/55 text-white px-2 py-0.5 backdrop-blur-sm",
    )}>
      {children}
    </span>
  );
}

export function StoreBrowseCard({ store, className }: { store: BrowseStore; className?: string }) {
  const open = storeIsOpen(store);
  const sponsored = isSponsored(store);
  const closedLabel = formatNextOpen(store.nextOpenTime, store.acceptingOrders);
  /* A mix & match deal is the most compelling thing a shop can advertise, and
     it was reaching the card as nothing at all. It sits alongside the auto-apply
     offers in the same ribbon rather than getting a competing badge. */
  const offers = [
    ...(store.mixMatchDeals ?? []).map((d) => `Any ${d.qty} for £${(d.pricePence / 100).toFixed(2)}`),
    ...(store.activeOffers ?? []).map(formatOfferWithMin),
  ];
  const deliveryMins = store.currentDeliveryMins ?? store.deliveryTimeMins;
  const collectionMins = store.currentCollectionMins ?? store.collectionTimeMins;
  const cuisine = (store.categories?.length ? store.categories : [store.category])
    .filter(Boolean).join(" · ");
  const distMi = store.distMi;

  return (
    <Link href={storeHref(store)} className={cn("group block h-full", FOCUS_RING, !(store.bannerUrl || store.coverPosterUrl) && "h-auto self-start", className)}
      aria-label={[store.name, cuisine, store.town, sponsored ? "Sponsored" : null, open ? "open" : "closed"].filter(Boolean).join(", ")}>
      <div className={cn(sponsored && "boosted-card h-full !rounded-[26px]")}><article className="relative h-full flex flex-col bg-white rounded-[24px] overflow-hidden border border-slate-200/80 shadow-[0_4px_20px_#10233d08] transition-shadow group-hover:shadow-[0_10px_30px_#10233d12]">
        {(store.bannerUrl || store.coverPosterUrl) && <div className="relative aspect-[1.7/1] bg-slate-100 overflow-hidden">
          <ShopCover store={store} priority={1} alt={`${store.name} banner`} className="w-full h-full transition-transform duration-500 group-hover:scale-[1.03]"/>
          {!open && <div className="absolute inset-0 bg-black/45 grid place-items-center"><span className="bg-white px-4 py-2 rounded-full text-sm font-semibold">{closedLabel}</span></div>}
          {store.imageUrl && <img src={store.imageUrl} alt={`${store.name} logo`} className="absolute bottom-3 left-3 w-14 h-14 rounded-xl border-2 border-white bg-white shadow-md object-contain" loading="lazy"/>}
        </div>}
        <div className={cn("p-4 sm:p-5 flex-1")}>
          {!(store.bannerUrl || store.coverPosterUrl) && store.imageUrl && <img src={store.imageUrl} alt={`${store.name} logo`} className="float-right ml-3 mb-2 w-14 h-14 rounded-xl border border-slate-200 bg-white object-contain" loading="lazy"/>}
          {!open && !(store.bannerUrl || store.coverPosterUrl) && <p className="text-xs font-semibold text-slate-600 mb-2">{closedLabel}</p>}
          <div className="flex items-start justify-between gap-3">
            <h3 className="text-[19px] leading-6 font-bold tracking-[-0.5px] text-[#1e3a5f]">{store.name}</h3>
            {(store.reviewCount ?? 0) > 0 && <span className="shrink-0 inline-flex items-center gap-1 rounded-full bg-slate-50 px-2 py-1 text-sm font-semibold text-[#1e3a5f]"><Star size={12} fill="currentColor" />{(store.avgRating ?? 0).toFixed(1)}</span>}
          </div>
          <p className="text-[13px] text-slate-500 mt-1">{[cuisine, store.town, distMi != null ? formatDistance(distMi) : null].filter(Boolean).join(" · ")}</p>
          {open && offers.length > 0 && <div className="mt-3 flex flex-wrap gap-1.5">{offers.slice(0, 2).map((offer, i) => <span key={i} className="max-w-full rounded-full border border-amber-200 bg-[#fff8e6] px-2.5 py-1 text-xs font-semibold text-[#1e3a5f]">{offer}</span>)}</div>}
          <p className="text-[13px] text-slate-700 mt-3 pt-3 border-t border-slate-100 flex flex-wrap gap-x-3 gap-y-1">
            {store.offersDelivery && <span>{deliveryLine(store, deliveryMins)}</span>}
            {store.offersCollection && <span>{collectionMins} min collection</span>}
          </p>
          {(sponsored || store.multiStoreEligible) && <p className="text-[11px] text-slate-500 mt-2">{[sponsored ? "Sponsored" : null, store.multiStoreEligible ? "Multi-shop available" : null].filter(Boolean).join(" · ")}</p>}
        </div>
      </article></div>
    </Link>
  );
}

/**
 * Dense row — the browse page's "list" view, which the storefront has always
 * offered and which is genuinely the better mode for scanning forty shops on
 * a wide screen. Same data and same link target as the card; only the
 * arrangement differs.
 */
export function StoreBrowseRow({ store, className }: { store: BrowseStore; className?: string }) {
  const open = storeIsOpen(store);
  const sponsored = isSponsored(store);
  const closedLabel = formatNextOpen(store.nextOpenTime, store.acceptingOrders);
  /* A mix & match deal is the most compelling thing a shop can advertise, and
     it was reaching the card as nothing at all. It sits alongside the auto-apply
     offers in the same ribbon rather than getting a competing badge. */
  const offers = [
    ...(store.mixMatchDeals ?? []).map((d) => `Any ${d.qty} for £${(d.pricePence / 100).toFixed(2)}`),
    ...(store.activeOffers ?? []).map(formatOfferWithMin),
  ];
  const deliveryMins = store.currentDeliveryMins ?? store.deliveryTimeMins;
  const collectionMins = store.currentCollectionMins ?? store.collectionTimeMins;
  const cuisine = (store.categories?.length ? store.categories : [store.category])
    .filter(Boolean).join(" · ");

  const distMi = store.distMi;
  return <Link href={storeHref(store)} className={cn(FOCUS_RING, "block", className)}>
    <div className={cn(sponsored && "boosted-card !rounded-[26px]")}><article className={cn("relative flex gap-4 p-4 border border-slate-200/80 bg-white rounded-[24px] shadow-[0_4px_20px_#10233d08]", sponsored && "px-4")}>
      {(store.bannerUrl || store.coverPosterUrl || store.imageUrl) && <div className={cn("relative shrink-0 self-start rounded-2xl overflow-hidden bg-slate-100", (store.bannerUrl || store.coverPosterUrl) ? "w-28 sm:w-44 aspect-[1.2/1]" : "w-14 h-14")}>
        {(store.bannerUrl || store.coverPosterUrl) ? <ShopCover store={store} priority={1} alt={`${store.name} banner`} className="w-full h-full" /> : <img src={store.imageUrl!} alt={`${store.name} logo`} loading="lazy" className="w-full h-full object-contain" />}
        {(store.bannerUrl || store.coverPosterUrl) && store.imageUrl && <img src={store.imageUrl} alt={`${store.name} logo`} className="absolute bottom-2 left-2 w-10 h-10 rounded-lg border-2 border-white bg-white object-contain" loading="lazy"/>}
      </div>}
      <div className="flex-1 min-w-0"><h3 className="text-lg font-bold tracking-tight text-[#1e3a5f]">{store.name}</h3>
        <p className="text-xs text-slate-500 mt-1">{[cuisine, store.town, distMi != null ? formatDistance(distMi) : null].filter(Boolean).join(" · ")}</p>
        <div className="mt-2"><StarRating avgRating={store.avgRating} reviewCount={store.reviewCount} /></div>
        <p className="text-xs text-slate-600 mt-2">{!open ? closedLabel : [store.offersDelivery ? deliveryLine(store, deliveryMins) : null, store.offersCollection ? `${collectionMins} min collection` : null].filter(Boolean).join(" · ")}</p>
        {offers.length > 0 && <p className="text-xs font-semibold text-[#1e3a5f] mt-2">{offers.slice(0,2).join(" · ")}</p>}
        {(sponsored || store.multiStoreEligible) && <p className="text-[11px] text-slate-500 mt-2">{[sponsored ? "Sponsored" : null, store.multiStoreEligible ? "Multi-shop available" : null].filter(Boolean).join(" · ")}</p>}
      </div>
    </article></div>
  </Link>;
}

export function StoreBrowseCardSkeleton() {
  return (
    <div className={cn(RADII.md, "h-full flex flex-col overflow-hidden bg-white border border-slate-200/80")}>
      <div className="aspect-[1.65/1] skeleton-shimmer bg-slate-200/70" />
      <div className="flex flex-col flex-1 gap-1.5 p-3">
        <div className="h-[21px] flex items-center"><div className="h-4 w-2/3 rounded skeleton-shimmer bg-slate-200/70" /></div>
        <div className="h-[14px] flex items-center"><div className="h-3 w-1/2 rounded skeleton-shimmer bg-slate-200/70" /></div>
        <div className="h-[16px] flex items-center"><div className="h-3 w-1/3 rounded skeleton-shimmer bg-slate-200/70" /></div>
        <div className="flex gap-1.5 mt-auto pt-1">
          <div className="h-[18px] w-20 rounded-full skeleton-shimmer bg-slate-200/70" />
          <div className="h-[18px] w-16 rounded-full skeleton-shimmer bg-slate-200/70" />
        </div>
      </div>
    </div>
  );
}

export function StoreBrowseRowSkeleton() {
  return <div className="flex gap-4 py-5 border-b border-slate-100 animate-pulse"><div className="w-28 sm:w-44 aspect-[1.2/1] shrink-0 rounded-2xl bg-slate-100" /><div className="flex-1 space-y-3 pt-2"><div className="h-5 w-2/3 rounded bg-slate-100" /><div className="h-3 w-1/2 rounded bg-slate-100" /><div className="h-3 w-1/3 rounded bg-slate-100" /></div></div>;
}
